import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, ChevronDown, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES, BRAND, CONTACT } from "@/data/site";
import { scrollToId } from "@/lib/scroll";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.25 } },
};
const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { type: "spring", stiffness: 80, damping: 18 } },
};

const HEADLINE = ["BELLEVUE'S", "BEST SPORTS", "BAR & GRILL"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Alley Pub interior"
          className="h-full w-full object-cover"
          style={{ filter: "brightness(0.55)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/60 to-[#0B132B]/30" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8 pb-16 md:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          className="mb-5"
        >
          <span
            data-testid="cowboys-badge"
            className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm px-4 py-2 shadow-[0_0_24px_rgba(30,140,215,0.25)]"
          >
            <Star className="h-3.5 w-3.5 text-primary fill-primary" />
            <span className="font-label text-[10px] md:text-xs text-[#F8F5F2] tracking-[0.22em]">
              Nashville's Official Dallas Cowboys Sports Bar
            </span>
            <Star className="h-3.5 w-3.5 text-primary fill-primary" />
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-primary" />
          <span className="font-label text-xs text-primary">
            {CONTACT.neighborhood} • Nashville, TN
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display text-[#F8F5F2] text-6xl sm:text-7xl lg:text-[8.5rem]"
        >
          {HEADLINE.map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} className="block">
                {i === 1 ? (
                  <>
                    BEST <span className="text-primary">SPORTS</span>
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-7 max-w-xl text-base md:text-lg text-[#F8F5F2]/80 leading-relaxed"
        >
          Great drinks, great food, great fun. Live music, karaoke and every
          game on the screens — right in Bellevue.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <Button
            data-testid="hero-offerings-btn"
            onClick={() => scrollToId("offerings")}
            className="rounded-full h-12 px-8 font-label text-xs hover:-translate-y-0.5 active:scale-95 transition-transform"
          >
            See What We Offer <ArrowRight className="h-4 w-4" />
          </Button>
          <a href={CONTACT.mapsUrl} target="_blank" rel="noreferrer">
            <Button
              data-testid="hero-directions-btn"
              variant="outline"
              className="rounded-full h-12 px-8 font-label text-xs border-white/25 bg-white/5 text-[#F8F5F2] hover:bg-white/10 hover:text-primary w-full sm:w-auto"
            >
              <MapPin className="h-4 w-4" /> Visit Us Today
            </Button>
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#F8F5F2]/50"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ChevronDown className="h-6 w-6" />
      </motion.div>
    </section>
  );
}
